/**
 * Small pieces every showings panel shares: the card frame, the inline error, the field styling and
 * the report channel through which a panel tells ShowingsSetup it is saving or has a form open (so
 * "Save and continue" and the unsaved-changes prompt know about it).
 */

import { useEffect, type ReactNode } from 'react';
import { cn } from '../../../../lib/utils';

export type PanelReport = (kind: 'saving' | 'open' | 'dirty', value: boolean) => void;

/**
 * Keeps ShowingsSetup informed of a panel's saving, form-open and unsaved-edit state; clears all three
 * on unmount.
 */
export function usePanelReport(report: PanelReport, saving: boolean, formOpen: boolean, dirty = false): void {
  useEffect(() => report('saving', saving), [report, saving]);
  useEffect(() => report('open', formOpen), [report, formOpen]);
  useEffect(() => report('dirty', dirty), [report, dirty]);
  useEffect(
    () => () => {
      report('saving', false);
      report('open', false);
      report('dirty', false);
    },
    [report]
  );
}

export const FIELD_CLASS =
  'min-h-[44px] w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60';

export const LINK_BUTTON_CLASS =
  'inline-flex min-h-[44px] items-center rounded-md px-2 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

export const DANGER_LINK_BUTTON_CLASS =
  'inline-flex min-h-[44px] items-center rounded-md px-2 text-sm font-medium text-destructive underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

interface ShowingsCardProps {
  title: string;
  description?: ReactNode;
  /** Right-aligned header content (a badge or a button). */
  action?: ReactNode;
  titleAdornment?: ReactNode;
  children: ReactNode;
  className?: string;
  'data-testid': string;
}

export function ShowingsCard({
  title,
  description,
  action,
  titleAdornment,
  children,
  className,
  'data-testid': testId,
}: ShowingsCardProps) {
  return (
    <section data-testid={testId} className={cn('rounded-lg border border-border bg-card p-4 sm:p-5', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            {titleAdornment}
          </div>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function InlineError({ message, testId }: { message: string; testId: string }) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive"
    >
      {message}
    </div>
  );
}
